import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

export type DrawerPagerProps = {
  currentIndex: number;
  total: number;
  onPrev: () => void;
  onNext: () => void;
  label?: string;
};

/**
 * DrawerPager — Shared primitive for navigating between items in the drawer.
 *
 * Renders chevron buttons and a "N of M" counter. Domain-specific logic
 * (navigation URLs, highlight dispatches) lives in wrapper components
 * (ObjectivePager, ResultPager).
 */
export function DrawerPager({ currentIndex, total, onPrev, onNext, label }: DrawerPagerProps) {
  const position = currentIndex >= 0 ? currentIndex + 1 : null;
  const canGoPrev = currentIndex > 0;
  const canGoNext = currentIndex >= 0 && currentIndex < total - 1;

  if (total === 0 || position === null) {
    return null;
  }

  return (
    <nav
      className="flex items-center gap-1 text-sm text-gray-600"
      aria-label={label ?? 'Item navigation'}
    >
      <button
        onClick={onPrev}
        disabled={!canGoPrev}
        aria-label="Previous"
        className={`p-0.5 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed`}
      >
        <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" />
      </button>
      <span className="select-none tabular-nums">
        {position} of {total}
      </span>
      <button
        onClick={onNext}
        disabled={!canGoNext}
        aria-label="Next"
        className={`p-0.5 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed`}
      >
        <ChevronRightIcon className="h-4 w-4" aria-hidden="true" />
      </button>
    </nav>
  );
}
